"use client"

import { useEffect } from "react"

import {
  type SituationEventContext,
  trackRecommendedProductViewed,
  trackRelatedGuideClicked,
  trackSituationCtaClicked,
  trackSituationPathSelected,
  useSituationPageView,
} from "@/lib/analytics"

// ---------------------------------------------------------------------------
// The ONE client component a situation page renders for its measurement
// (web/lib/situation-page-registry.ts; events in web/lib/analytics.ts).
//
// The eighteen situation pages are server components and stay that way: their
// copy is static, their metadata comes from the registry, and none of them
// needs client state. So the click and visibility measurement is DELEGATED
// rather than wired per element: the page marks the elements it wants
// measured with plain data attributes, and this component, rendered once per
// page, listens at the document and reads them back. No onClick props, no
// client conversion, one listener.
//
// The attribute contract a page writes:
//
//   data-situation-track="path"   data-path-id="<stable id>"
//                                 [data-path-label] [data-recommended-product]
//                                 [data-cta-module]           -> situation_path_selected
//   data-situation-track="guide"  data-guide-kind="article|situation|offer"
//                                 [data-cta-module]           -> related_guide_clicked
//   data-situation-track="cta"    data-cta-module="hero|router|final_cta"
//                                 [data-recommended-product]  -> situation_cta_clicked
//   data-situation-product="/x"   [data-situation-diagnosis]  -> recommended_product_viewed,
//                                 once, when at least half the card is on screen
//
// The related situations in the rail carry the guide attributes like any other
// link (data-guide-kind="situation", data-cta-module="related_situations").
// ---------------------------------------------------------------------------

function pathOf(href: string): string {
  const cut = href.search(/[?#]/)
  return cut >= 0 ? href.slice(0, cut) : href
}

type Props = {
  /** Built once by the server page with situationEventContext(slug). */
  context: SituationEventContext
}

export function SituationTracking({ context }: Props) {
  useSituationPageView(context)

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const target = event.target
      if (!(target instanceof Element)) return
      const anchor = target.closest("a[href]")
      if (!(anchor instanceof HTMLAnchorElement)) return
      const href = anchor.getAttribute("href") ?? ""
      const data = anchor.dataset

      switch (data.situationTrack) {
        case "path":
          trackSituationPathSelected(context, {
            path_id: data.pathId ?? pathOf(href),
            ...(data.pathLabel ? { path_label: data.pathLabel } : {}),
            ...(data.recommendedProduct ? { recommended_product: data.recommendedProduct } : {}),
            ...(data.ctaModule ? { cta_module: data.ctaModule } : {}),
          })
          return
        case "guide":
          trackRelatedGuideClicked(context, {
            href,
            ...(isGuideKind(data.guideKind) ? { guide_kind: data.guideKind } : {}),
            ...(data.ctaModule ? { cta_module: data.ctaModule } : {}),
          })
          return
        case "cta":
          trackSituationCtaClicked(context, {
            cta_module: data.ctaModule ?? "unknown",
            href,
            ...(data.recommendedProduct ? { recommended_product: data.recommendedProduct } : {}),
          })
          return
        default:
          break
      }
    }

    document.addEventListener("click", onClick)
    return () => document.removeEventListener("click", onClick)
  }, [context])

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return
    const cards = Array.from(document.querySelectorAll<HTMLElement>("[data-situation-product]"))
    if (cards.length === 0) return
    const seen = new WeakSet<Element>()
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting || seen.has(entry.target)) continue
          seen.add(entry.target)
          observer.unobserve(entry.target)
          const el = entry.target as HTMLElement
          const product = el.dataset.situationProduct
          if (!product) continue
          trackRecommendedProductViewed(context, {
            recommended_product: product,
            ...(el.dataset.situationDiagnosis ? { diagnosis: el.dataset.situationDiagnosis } : {}),
            cta_module: el.dataset.ctaModule ?? "router",
          })
        }
      },
      { threshold: 0.5 },
    )
    for (const card of cards) observer.observe(card)
    return () => observer.disconnect()
  }, [context])

  return null
}

function isGuideKind(value: string | undefined): value is "situation" | "article" | "offer" {
  return value === "situation" || value === "article" || value === "offer"
}
